import React, { useEffect } from "react";
import L from "leaflet";
import { useMap } from "react-leaflet";
import { max } from "date-fns";

interface LegendProps {
    getColor: (value: number, dataset: string) => string;
    minValue?: number;
    maxValue?: number;
    categories:{[key: string]: number};
    "polygon_type": "Continuous" | "Discrete";
    bins?: number | 0;
    "polygon_values": { [key: string]: string; };
    "current_dataset": string;
    "attribute_legend_labels": string;
    "is_log"? : boolean
}

// Helper function to format numbers with superscript exponents for Chart.js
const formatWithPrettyExponents = (value: string | number, useLogFormat: boolean): string => {
    // Only apply pretty formatting if is_log is true
    if (!useLogFormat) {
      // Standard formatting without exponents
      if (typeof value === 'string' && value.includes(' - ')) {
        const parts = value.split(' - ');
        const formattedParts = parts.map(part => {
          const num = Number(part);
          if (!isNaN(num)) {
            return Math.abs(num) > 1000000 ? num.toExponential(2) : num.toFixed(2);
          }
          return part; // Keep as is if not a number
        });
        return formattedParts.join(" - ");
      }
      
      const num = Number(value);
      if (isNaN(num)) return String(value);
      return Math.abs(num) > 1000000 ? num.toExponential(2) : num.toFixed(2);
    }
    
    // Unicode superscript characters for exponents
    const superscriptMap: {[key: string]: string} = {
      '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', 
      '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
      '-': '⁻', '.': '·'
    };
    
    // Convert a number to superscript string
    const toSuperscript = (num: number): string => {
      const numStr = num.toFixed(2);
      return [...numStr].map(char => superscriptMap[char] || char).join('');
    };
    
    // Check if the label appears to be a simple negative number range (like -2.197 - -1.81)
    // rather than an exponent range
    if (typeof value === 'string' && value.includes(' - ')) {
      const parts = value.split(' - ');
      
      // Check if both parts are numbers
      if (parts.every(part => !isNaN(Number(part)))) {
        const numbers = parts.map(Number);
        
        // Process each part and format with exponents
        const formattedParts = numbers.map(num => {
          return `10${toSuperscript(num)}`;
        });
        
        return formattedParts.join(" - ");
      }
    }
    
    // For single values
    const num = Number(value);
    if (isNaN(num)) return String(value); // Not a number, return as is
    
    return `10${toSuperscript(num)}`;
  };

const Legend: React.FC<LegendProps> = ({ getColor, minValue = 0, maxValue = 1, polygon_type = "Discrete", bins = 0, polygon_values, current_dataset, attribute_legend_labels,categories, is_log }) => {
    const map = useMap(); // Get the map instance
    let minString = String(minValue.toFixed(2));
    let maxString = String(maxValue.toFixed(2));
    if (minValue > 1000000) { minString = minValue.toExponential(3);}
    if (maxValue > 1000000) { maxString = maxValue.toExponential(3);}

    if(is_log){
        minString = formatWithPrettyExponents(minValue,true);
        maxString = formatWithPrettyExponents(maxValue,true);
    }

    useEffect(() => {
        if (!map) return;

        const legend = new L.Control({ position: "bottomright" });

        legend.onAdd = () => {
            const div = L.DomUtil.create("div", "info legend");
            let labels: string[] = [];

            if (bins === 0) {
                // Continuous Color Scale (Gradient)
                const legendTitle = attribute_legend_labels; //attributes_list.titles[attributes_list.names.indexOf(attribute_name)];
                div.innerHTML = `
        <strong>${legendTitle}</strong><br>
        <div style="width: 200px; height: 20px; background: linear-gradient(to right, 
            ${getColor(0.0, current_dataset)}, 
            ${getColor(0.25, current_dataset)}, 
            ${getColor(0.5, current_dataset)}, 
            ${getColor(0.75, current_dataset)}, 
            ${getColor(1.0, current_dataset)}
        ); border: 1px solid black;"></div>
        <div style="display: flex; justify-content: space-between; width: 200px;">
            <span>${minString}</span>
            <span>${maxString}</span>
        </div>
    `;

            } else {
                // Discrete Categories
     
                const step = (maxValue - minValue) / (bins - 1); // Divide range into equal bins
                const grades = Array.from({ length: bins }, (_, i) => minValue + i * step);
                for (let i = 0; i < grades.length; i++) {
                    
                    const value = grades[i] / maxValue; // Normalize for getColor function

                    const key = Object.keys(categories).find(k => categories[k] === i);

                    labels.push(
                        `<i style="background:${getColor(value, current_dataset)}; width: 20px; height: 20px; display: inline-block; margin-right: 5px;"></i> 
                        ${key}`
                    );
                }

                const legendTitle = attribute_legend_labels; // attributes_list.titles[attributes_list.names.indexOf(attribute_name)];
                div.innerHTML = `<strong>${legendTitle}</strong><br>${labels.join("<br>")}`;
            }
            return div;
        };

        legend.addTo(map);

        return () => {
            map.removeControl(legend);
        };
    }, [map, getColor, minValue, maxValue]);

    return null;
};

export default Legend;
